/**
 * 👁 Peek, the pure half (ROOM_VERBS_PEEK_SPEC_2026_09_18 §4; Nina's rulings
 * D1 = the words go UNDER the art, D2 = a porthole, opened from an eye on each
 * RoomPicker card).
 *
 * No new data: every input is already in the status payload (the same
 * `presenceFor(roomId)` rows the plate and the picker faces read — spec §1.8).
 * Zero network, zero LLM, zero backend.
 *
 * ⭐⭐ Nothing here ever lands ON a picture (the no-text-on-art ruling). The
 *   caption is a list the component renders beneath the art.
 * Player copy: short, punchy, no em dashes.
 */
export const PEEK_MAX_LINES = 6;

/** here = [{ name, doing?, avatar? }] → caption rows, capped so a full house
 *  never pushes the art off a phone. `more` = how many were left out. */
export function peekCaption(here, max = PEEK_MAX_LINES) {
  const list = (Array.isArray(here) ? here : []).filter((h) => h && typeof h.name === 'string' && h.name.trim());
  const shown = list.slice(0, Math.max(0, max)).map((h) => ({
    name: h.name,
    doing: typeof h.doing === 'string' ? h.doing.trim() : '',
  }));
  return { lines: shown, more: Math.max(0, list.length - shown.length), empty: list.length === 0 };
}

/** The line under an empty room. The main room is never "empty of the house".
 *  `inRoom` = the viewer is standing in it (/look, 09-24 Nina: "here"); the
 *  keyhole looks in from outside, so it stays "in there". */
export function peekEmptyLine(roomId, inRoom = false) {
  if (inRoom) return 'Nobody is here right now.';
  return roomId === 'house' ? 'Nobody in the main room right now.' : 'Nobody in there right now.';
}

/** Can this picker card be peeked into? Not the room you are standing in (the
 *  plate's own peek covers that), and only a card that has a name to show. */
export function canKeyhole(card) {
  return !!(card && card.id && !card.you && card.name);
}

// ── 🦝 Where's Gerald (spec §4 P5 · D3 eyes until tapped, then the whole
// raccoon · D4 streak only, per device) ─────────────────────────────────────
// Zero network, zero backend state. His room is a DATE HASH over the open
// rooms, byte-for-byte the backend's houseSnap.geraldRoomFor (the polaroid
// caption mentions him server-side, so the two MUST agree:
// roomSnapParity.test.js requires the backend module and pins it).

/** FNV-1a 32 (houseCircadian.hashSeed / houseSnap.hash32). */
function hash32(str) {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Which open room he is in on a HOUSE day (clock.day, never the viewer's
 *  Date), or null. Order-independent, never the main room. */
export function geraldRoomFor(houseDay, openRoomIds) {
  if (typeof houseDay !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(houseDay)) return null;
  const ids = [...new Set((Array.isArray(openRoomIds) ? openRoomIds : [])
    .filter((id) => typeof id === 'string' && id && id !== 'house'))].sort();
  if (!ids.length) return null;
  return ids[hash32(`${houseDay}|gerald`) % ids.length];
}

/** Where his eyes sit ON each painting, in % of the picture (x from the left,
 *  y from the top). Authored 09-19 against the real art: every night / rain /
 *  autumn take is anchored on the day composition (checked on four packs), so
 *  one coordinate serves them all. Each one is the shadow UNDER something, and
 *  carries the SAME words the backend puts in the polaroid caption (`where`,
 *  pinned equal to houseSnap.GERALD_SPOTS by roomSnapParity.test.js). */
export const GERALD_HOTSPOTS = {
  den: { x: 55, y: 71, where: 'under the coffee table' },
  gym: { x: 18, y: 80, where: 'under the weight rack' },
  attic: { x: 71, y: 70, where: 'behind a stack of boxes' },
  music: { x: 81, y: 79, where: 'under the piano' },
  porch: { x: 20, y: 80, where: 'under the porch swing' },
  dining: { x: 50, y: 77, where: 'under the table' },
  garage: { x: 19, y: 71, where: 'between the boxes' },
  office: { x: 53, y: 75, where: 'under the desk' },
  library: { x: 40, y: 77, where: 'under the armchair' },
  sunroom: { x: 85, y: 78, where: 'among the plant pots' },
  bathroom: { x: 50, y: 80, where: 'under the stool' },  // the tub is taken, by a goose
  workshop: { x: 53, y: 72, where: 'under the workbench' },
  greenhouse: { x: 29, y: 80, where: 'under the potting bench' },
  // 🌕 09-26 the roof (opened for the Harvest Moon; Nina picked the TOWN set):
  // in the dark gap between the chimney's right edge and the ivy pot, authored
  // on the 09-26 night take B2 (dusk + day are repaints of it: one coordinate).
  roof: { x: 86, y: 55, where: 'behind the chimney' },
};
/** The porthole crops its picture to a circle, so painting coordinates mean
 *  nothing there: he peers out of the dark lower-left of the glass instead. */
export const KEYHOLE_GERALD = { x: 32, y: 66 };

// ── 🔍 The peek glass (09-19, Nina: a magnifying glass is how you find him;
// option C = the brass rim warms as you close in) ────────────────────────────
// Pure geometry, all in PICTURE pixels. The component owns the DOM; every
// number it needs comes from here so jest can pin the rules without a layout.
export const GLASS_ZOOM = 2;
export const GLASS_TOUCH_LIFT = 84;   // px the glass rides ABOVE a thumb, so the hand never covers the look
/** ⭐ How far away the rim starts to warm, as a share of the picture's width.
 *  Kept tight ON PURPOSE: at 0.4 one lazy sweep answers "is he in this room
 *  at all?" and the thirteen-room hunt is over before it starts. */
export const GLASS_WARM_RANGE = 0.2;
const RIM_COLD = [201, 161, 90];      // brass
const RIM_HOT = [255, 138, 60];       // ember

/** Glass radius for a picture `w` px wide. Scales with the picture: a phone's
 *  painting is ~200px tall, and a fixed 60px radius covered most of it and
 *  spilled onto the words underneath (the 09-19 real-browser look). */
export function glassRadius(w) {
  const n = Number(w);
  return n > 0 ? Math.round(Math.max(44, Math.min(75, n * 0.13))) : 75;
}

/** Where the glass CENTER sits for a pointer at (clientX, clientY) over the
 *  picture's `rect`. Clamped to the picture; a touch is lifted above the thumb. */
export function glassCenter(clientX, clientY, rect, isTouch) {
  if (!rect || !(rect.width > 0) || !(rect.height > 0)) return null;
  const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
  const y = Math.max(0, Math.min(rect.height, clientY - rect.top - (isTouch ? GLASS_TOUCH_LIFT : 0)));
  return { x, y };
}

const spotPx = (spot, w, h) => ({ x: (Number(spot.x) / 100) * w, y: (Number(spot.y) / 100) * h });
function glassDistance(center, spot, w, h) {
  if (!center || !spot || !(w > 0) || !(h > 0)) return Infinity;
  const s = spotPx(spot, w, h);
  return Math.hypot(center.x - s.x, center.y - s.y);
}

/** Is he IN the glass? The glass shows radius / zoom picture-pixels around its
 *  center; 0.8 of that keeps a catch honest (eyes clearly inside, not clipped
 *  by the rim). Never under 24px, the thumb-target floor. */
export function glassCatches(center, spot, w, h) {
  const reach = Math.max(24, (glassRadius(w) / GLASS_ZOOM) * 0.8);
  return glassDistance(center, spot, w, h) <= reach;
}

/** 0 (cold brass) → 1 (on top of him). */
export function rimWarmth(center, spot, w, h) {
  const d = glassDistance(center, spot, w, h);
  if (!Number.isFinite(d)) return 0;
  return Math.max(0, Math.min(1, 1 - d / (w * GLASS_WARM_RANGE)));
}

export function rimColor(t) {
  const k = Math.max(0, Math.min(1, Number(t) || 0));
  const c = RIM_COLD.map((v, i) => Math.round(v + (RIM_HOT[i] - v) * k));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

// ── 🔍💬 Asides (09-19, Nina: "yes for asides, really funny/silly ones") ─────
// Hold the glass over the right object and the room says one line about it,
// UNDER the art (never on it). On in EVERY room EVERY day, his room included:
// an aside that appeared only where Gerald is not would be a tell.
// Zero network, zero LLM, zero state: an authored bank, and a day hash picks
// which of an object's lines is today's, so a regular sees them rotate.
// x / y = % of the painting, authored 09-19 on a 10% grid over the real art
// (one coordinate serves every take, same as GERALD_HOTSPOTS). Pinned by
// roomAsides.test.js: every spot is inside the picture, clear of his hotspot,
// clear of its neighbours, and no line carries an em dash.
// Player copy: short, punchy, no em dashes.
export const PEEK_ASIDES = {
  bathroom: [
    { id: 'goose', x: 70, y: 49, lines: ['The goose is watching you back.', 'The goose has been in there since Tuesday. Nobody asks.'] },
    { id: 'mirror', x: 37, y: 17, lines: ['Honest mirror. It says you look nosy.', 'The mirror has seen things. It is not telling.'] },
    { id: 'robe', x: 10, y: 45, lines: ['Nobody knows whose robe that is. Everybody has worn it.'] },
  ],
  porch: [
    { id: 'mug', x: 81, y: 65, lines: ['That mug has been out here three days. It is load bearing now.', 'Still warm. Suspicious.'] },
    { id: 'swing', x: 19, y: 60, lines: ['The swing creaks on its own sometimes. It is just saying hi.'] },
    { id: 'sky', x: 49, y: 36, lines: ['That is a lot of stars for one porch.', 'Best seat in the house. Not technically in the house.'] },
  ],
  den: [
    { id: 'fire', x: 27, y: 60, lines: ['The fire is doing its best.', 'Somebody keeps feeding it. Nobody admits it.'] },
    { id: 'pillows', x: 77, y: 44, lines: ['Fourteen pillows. Room for one person.', 'The blanket pile may contain a resident.'] },
    { id: 'maps', x: 89, y: 12, lines: ['A map of somewhere nobody here has been. Framed anyway.'] },
  ],
  dining: [
    { id: 'fruit', x: 52, y: 57, lines: ['The fruit is decorative. Ask the last person who tried it.'] },
    { id: 'lamp', x: 49, y: 25, lines: ['The lamp hangs exactly at forehead height. On purpose, probably.'] },
    { id: 'garlic', x: 86, y: 29, lines: ['The garlic is for cooking. Mostly.'] },
  ],
  library: [
    { id: 'floorbooks', x: 60, y: 81, lines: ['Someone is reading six books at once and finishing none.'] },
    { id: 'lanterns', x: 24, y: 86, lines: ['The lanterns keep a schedule only they understand.'] },
    { id: 'shelf', x: 11, y: 41, lines: ['Alphabetical by vibes.', 'Third shelf, fourth book. Overdue since forever.'] },
  ],
  attic: [
    { id: 'dressform', x: 62, y: 52, lines: ['She has stood there for years. Great posture.', 'The mannequin did not move. You looked away, that is all.'] },
    { id: 'window', x: 49, y: 47, lines: ['Best window in the house. Worst stairs.'] },
    { id: 'boxes', x: 11, y: 66, lines: ['Every box says MISC.'] },
  ],
  workshop: [
    { id: 'birdhouse', x: 22, y: 49, lines: ['The birdhouse has a waiting list.', 'Built for a bird. Inspected by a raccoon.'] },
    { id: 'pegboard', x: 51, y: 32, lines: ['Every tool has an outline. Three outlines have no tool.'] },
    { id: 'sawdust', x: 76, y: 57, lines: ['The sawdust is structural at this point.'] },
  ],
  sunroom: [
    { id: 'chair', x: 26, y: 61, lines: ['The good chair. There is a sign up sheet, in spirit.'] },
    { id: 'hills', x: 58, y: 33, lines: ['The view does most of the decorating.'] },
  ],
  greenhouse: [
    { id: 'flowers', x: 38, y: 42, lines: ['The flowers know they are the favorites.'] },
    { id: 'bigleaf', x: 90, y: 42, lines: ['That one grew a new leaf out of spite.'] },
    { id: 'seedtray', x: 74, y: 93, lines: ['Labeled "surprise". Planted by someone confident.'] },
  ],
  garage: [
    { id: 'outline', x: 49, y: 80, lines: ['The car has been gone a while. The outline is doing fine.', 'A chalk outline of a car. No car was harmed.'] },
    { id: 'bike', x: 69, y: 59, lines: ['The bike has one gear: eventually.'] },
    { id: 'plant', x: 15, y: 27, lines: ['The garage plant is thriving. Nobody waters it. Do not ask.'] },
  ],
  gym: [
    { id: 'cloud', x: 46, y: 62, lines: ['That is either chalk dust or ambition. Hard to say.'] },
    { id: 'rope', x: 80, y: 41, lines: ['The jump rope holds the house record. It was alone at the time.'] },
    { id: 'mat', x: 50, y: 86, lines: ['The mat is for stretching. And lying down. Mostly lying down.'] },
  ],
  music: [
    { id: 'mic', x: 5, y: 44, lines: ['The mic is on. It is always on. Be cool.'] },
    { id: 'notes', x: 62, y: 24, lines: ['A few notes got loose. They will find their way back.'] },
    { id: 'guitar', x: 28, y: 67, lines: ['Four chords. Infinite confidence.'] },
    { id: 'beanbag', x: 59, y: 76, lines: ['The beanbag takes people. It gives some of them back.'] },
  ],
  office: [
    { id: 'screen', x: 53, y: 39, lines: ['The screen is glowing. Nobody is working. Both things are fine.'] },
    { id: 'chart', x: 39, y: 11, lines: ['A map of the stars. Someone circled one and wrote "mine".'] },
    { id: 'teapot', x: 76, y: 47, lines: ['A teapot in the office. The house may have a situation.'] },
  ],
  // 🌕 09-26 the roof, authored on the 09-26 night take B2 (the town set).
  roof: [
    { id: 'telescope', x: 33, y: 45, lines: ['The telescope is aimed at the neighbors. Allegedly by accident.', 'Someone taped a star chart to it. Upside down.'] },
    { id: 'hatch', x: 20, y: 78, lines: ['The hatch is propped open with a spoon. So far, so good.', 'Nobody closes the hatch. The attic has opinions about that.'] },
    { id: 'snacks', x: 38, y: 81, lines: ['The pie is for the moon. Nobody told the pie.', 'Two mugs, one thermos, and a treaty about the last cookie.'] },
    { id: 'town', x: 60, y: 52, lines: ['Every window in town is watching something. Probably not this.'] },
    { id: 'quilts', x: 70, y: 68, lines: ['Room for everyone. Everyone still fights for the middle.'] },
    { id: 'footstool', x: 50, y: 93, lines: ['The footstool is load bearing. Do not ask what it holds.'] },
    { id: 'lights', x: 71, y: 33, lines: ['The string lights were a gift. Nobody remembers from whom.'] },
  ],
};

/** The aside the glass is over, or null → { key, line }. An object counts once
 *  it is IN the glass (the radius the glass shows). `holding` = the key now on
 *  screen: it stays until the glass is well clear of it (1.8x), so a line never
 *  flickers at the edge of its object. */
export function peekAside(roomId, center, w, h, dayKey, holding = null) {
  const spots = PEEK_ASIDES[roomId];
  if (!spots || !center || !(w > 0) || !(h > 0)) return null;
  const reach = Math.max(24, glassRadius(w) / GLASS_ZOOM);
  let best = null;
  for (const sp of spots) {
    const d = glassDistance(center, sp, w, h);
    const key = `${roomId}.${sp.id}`;
    if (d <= (key === holding ? reach * 1.8 : reach) && (!best || d < best.d)) best = { d, sp, key };
  }
  if (!best) return null;
  const { lines } = best.sp;
  return { key: best.key, line: lines[hash32(`${dayKey || ''}|${best.key}`) % lines.length] };
}

const GERALD_KEY = 'house_gerald_v1';
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const EMPTY_GERALD = { lastDay: null, streak: 0, best: 0 };
const dayIndex = (k) => Math.round(Date.parse(`${k}T00:00:00Z`) / 86400e3); // a CONVERSION of the house day, not a clock read

/** Pure. prev + the house day he was found on → the next saved state. */
export function nextGeraldState(prev, day) {
  const ok = prev && typeof prev === 'object' && typeof prev.lastDay === 'string' && DAY_RE.test(prev.lastDay)
    && Number.isFinite(prev.streak) && prev.streak > 0;
  const p = ok ? { lastDay: prev.lastDay, streak: Math.floor(prev.streak), best: Math.max(Math.floor(prev.streak), Number.isFinite(prev.best) ? Math.floor(prev.best) : 0) } : null;
  if (typeof day !== 'string' || !DAY_RE.test(day)) return p || { ...EMPTY_GERALD };
  if (!p) return { lastDay: day, streak: 1, best: 1 };
  if (p.lastDay === day) return p;
  const streak = dayIndex(day) - dayIndex(p.lastDay) === 1 ? p.streak + 1 : 1;
  return { lastDay: day, streak, best: Math.max(p.best, streak) };
}

/** Per-device convenience only: it can come back empty or throw (private
 *  window, blocked site data), and the peek renders correctly without it. */
export function readGerald() {
  try {
    const raw = window.localStorage.getItem(GERALD_KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (v && typeof v.lastDay === 'string' && DAY_RE.test(v.lastDay) && Number.isFinite(v.streak) && v.streak > 0) {
      return { lastDay: v.lastDay, streak: Math.floor(v.streak), best: Math.max(Math.floor(v.streak), Number.isFinite(v.best) ? Math.floor(v.best) : 0) };
    }
  } catch (e) { /* fall through */ }
  return { ...EMPTY_GERALD };
}

export function markGeraldFound(day) {
  const next = nextGeraldState(readGerald(), day);
  try { window.localStorage.setItem(GERALD_KEY, JSON.stringify(next)); } catch (e) { /* the find still shows this session */ }
  return next;
}

/** Player copy: short, punchy, no em dashes. */
export function geraldLine(streak) {
  return streak > 1 ? `You found Gerald. ${streak} days running.` : 'You found Gerald.';
}

/** What a peek of `roomId` needs to draw him, or null for every room but
 *  today's (null = the peek renders the exact DOM it always has). */
export function geraldPeekProps(roomId, geraldRoom, houseDay, state) {
  if (!roomId || !geraldRoom || roomId !== geraldRoom || typeof houseDay !== 'string' || !DAY_RE.test(houseDay)) return null;
  const s = state && typeof state === 'object' ? state : EMPTY_GERALD;
  const found = s.lastDay === houseDay;
  return { found, streak: found ? (s.streak || 1) : 0, spot: GERALD_HOTSPOTS[roomId] || null };
}

// ── 🦝🧦 Lost & found (LOST_AND_FOUND_SPEC_2026_09_24, W2) ────────────────────
// Most mornings Gerald hides a resident's thing at HIS spot in a room; a visitor
// standing in that room finds it with the glass and hands it back. D7 (Nina
// 09-24 late): it looks like a polka-dot bundle OR a paper tag, rolled ONCE per
// case by the server (no emoji: it differs per device and reads as a sticker on
// the painting); the glint on the rim means "something lost is near", the warm
// rim stays Gerald's. It needs no table of its own: the hiding rooms ARE
// GERALD_HOTSPOTS (lostFound.test.js pins the backend's HIDE_ROOMS inside it).
export const LOST_LOOKS = ['bundle', 'tag'];

/** What a peek of `roomId` needs to draw the missing thing, or null.
 *  `lost` = the status payload's `house.lost`, which the server sends ONLY to a
 *  viewer standing in that room: `{ id, room, look }` while it is missing,
 *  `{ room, found: { owner, label, by } }` for a few minutes after a find.
 *  → { id, look, spot } | { found } | null */
export function lostPeekProps(roomId, lost) {
  if (!roomId || !lost || typeof lost !== 'object' || lost.room !== roomId) return null;
  if (lost.found && typeof lost.found === 'object') {
    const { owner, label, by } = lost.found;
    return (owner && label && by) ? { found: { owner: String(owner), label: String(label), by: String(by) } } : null;
  }
  const spot = GERALD_HOTSPOTS[roomId] || null;
  if (typeof lost.id !== 'string' || !lost.id || !spot) return null;
  return { id: lost.id, look: LOST_LOOKS.includes(lost.look) ? lost.look : LOST_LOOKS[0], spot };
}

/** The line under the picture after someone else handed it back. The finder
 *  reads the server's own words instead. Player copy: short, no em dashes. */
export function lostFoundLine(found) {
  if (!found || !found.by || !found.owner || !found.label) return '';
  return `${found.by} found ${found.owner}'s ${found.label}. It's back with ${found.owner}.`;
}

/** 🎬🍿 PAINTED SCREENS (LIGHTHOUSE_SCREENING_SPEC 09-20): where a room's art
 *  has a movie screen, in % of the painting: { x, y } = its top-left corner,
 *  { w, h } = its size. Measured on the approved lighthouse takes (dusk L and
 *  the night repaint agree; a repaint keeps layout). The picture view is the
 *  ONLY place the painting has a measurable box, so the ▶ lives here.
 *  ⚠️ NOT one of the three parity-pinned peek tables (emitters · asides ·
 *  Gerald): a room may have a screen and none of those. */
export const SCREEN_RECTS = {
  lighthouse: { x: 64.8, y: 25.3, w: 14.3, h: 26.8 },
};
export function screenRectFor(roomId) {
  const r = roomId && SCREEN_RECTS[roomId];
  return (r && [r.x, r.y, r.w, r.h].every((n) => Number.isFinite(n) && n >= 0) && r.x + r.w <= 100 && r.y + r.h <= 100) ? r : null;
}

/** 🌕 THE MOON IN THE PICTURE (09-26, the Harvest Moon; Nina: "the view should
 *  look very nice"). No painting carries a moon ON PURPOSE: a painted full moon
 *  would lie on every other night of the month (the sky almanac's coherence
 *  rule). On a moon night the picture view draws one instead, in % of the
 *  painting: it starts low over the horizon when the event starts and climbs
 *  toward `top` over MOON_RISE_MS, shrinking a little as it goes (the big low
 *  orange kind first, small and pale later). Authored per painting like the
 *  hotspots above; a room without a spot, or a night without a moon event,
 *  draws nothing. ⚠️ NOT one of the three parity-pinned peek tables. */
export const MOON_SPOTS = {
  roof: { x: 48, y: 29, top: 12 },   // above the moonrise glow between the hills (09-26 night take B2)
  porch: { x: 57, y: 29, top: 17 },  // between the posts, over the water (autumn night take)
};
const MOON_TONES = { moon_viewing: 'harvest', blood_moon: 'blood' };
export const MOON_RISE_MS = 4 * 60 * 60 * 1000;
export function moonSkyFor(roomId, template, bornMs, nowMs) {
  const spot = roomId && MOON_SPOTS[roomId];
  const tone = template && MOON_TONES[template];
  if (!spot || !tone) return null;
  const t = Number(nowMs) - Number(bornMs);
  const p = Number.isFinite(t) ? Math.max(0, Math.min(1, t / MOON_RISE_MS)) : 0;
  const round = (n) => Math.round(n * 100) / 100;
  return { moon: { x: spot.x, y: round(spot.y - (spot.y - spot.top) * p), size: round(6.4 - 1.6 * p), tone, p: round(p) } };
}

// ── 🌕☁️🌠🏮 ROOF NIGHT (09-26, the Harvest Moon) ─────────────────────────────
// Pure timing + selection for the drawn sky: the twist cloud, meteors, the
// falling star, wish lanterns and the telescope. Every input is a feed row the
// page already holds, so every viewer derives the SAME answer (created_at, row
// ids) and the picture syncs its CSS with a negative animation-delay = elapsed
// seconds. Zero network, zero state. ⚠️ NOT one of the parity-pinned tables.
export const TWIST_CLOUD_MS = 6 * 60 * 1000;   // the cloud crosses the moon for 6 min after the twist card
export const STAR_WINDOW_MS = 60 * 1000;       // first /wish on the roof inside this catches the star
export const STAR_PERIOD_S = 6;                // one big streak across the sky every 6s while it is catchable
export const LANTERN_WINDOW_MS = 90 * 1000;    // a wish floats a lantern for 90s
export const LANTERN_MAX = 12;
export const WISH_MAX = 140;
/** The open sky of a painting, [x0, y0, x1, y1] in % (the roof's = its
 *  PEEK_EMITTERS stars box, pinned equal by roofMoonNight.test.js; a copy so
 *  this module never imports roomView). */
export const SKY_BOXES = { roof: [3, 3, 73, 30] };
/** The roof telescope (PEEK_ASIDES.roof telescope spot); reach = share of the picture's width. */
export const TELESCOPE = { x: 33, y: 45, reach: 0.07 };

const rowMs = (a) => { const t = new Date((a && a.created_at) || 0).getTime(); return Number.isFinite(t) ? t : 0; };
const rowsOf = (actions) => (Array.isArray(actions) ? actions : []);
const isWishRow = (a) => !!(a && a.type === 'space_command' && a.data && a.data.command === 'rv_wish' && a.data.room_verb === true);

/** ☁️ The twist cloud for a moon night: the newest twist card of the running
 *  event, while inside its 6 minutes → { id, start, elapsedSec, durSec }, else null. */
export function twistCloudFor(actions, liveEvent, nowMs) {
  if (!liveEvent || !MOON_TONES[liveEvent.template]) return null;
  const born = Number(liveEvent.born) || 0;
  let best = null;
  for (const a of rowsOf(actions)) {
    if (!a || a.type !== 'shared_content' || !a.data || a.data.is_event_twist !== true) continue;
    const t = rowMs(a);
    if (!t || t < born) continue;
    if (!best || t > best.t) best = { id: a.id, t };
  }
  if (!best) return null;
  const el = Number(nowMs) - best.t;
  if (!Number.isFinite(el) || el >= TWIST_CLOUD_MS) return null;
  return { id: String(best.id || best.t), start: best.t, elapsedSec: Math.max(0, el) / 1000, durSec: TWIST_CLOUD_MS / 1000 };
}

/** Does the cloud sit ON the moon now? The middle third of its crossing (the
 *  reduced-motion picture draws it there, still, and nowhere else). */
export function cloudCovers(cloud) {
  if (!cloud || !(cloud.durSec > 0)) return false;
  const k = cloud.elapsedSec / cloud.durSec;
  return k >= 1 / 3 && k < 2 / 3;
}

/** 🌠 Meteors: the room's sky box on a meteor-shower night, else null. */
export function meteorBoxFor(roomId, template) {
  return (template === 'meteor_shower' && roomId && SKY_BOXES[roomId]) ? SKY_BOXES[roomId] : null;
}

/** One meteor inside `box`: its head starts in the upper sky, it falls down-left
 *  or down-right. rnd = Math.random in the page, a fixed stream in tests. */
export function makeMeteor(box, rnd = Math.random, id = 0) {
  if (!Array.isArray(box) || box.length !== 4) return null;
  const [x0, y0, x1, y1] = box;
  const r2 = (n) => Math.round(n * 100) / 100;
  const left = rnd() < 0.5;
  const tilt = 12 + rnd() * 22;
  return {
    id,
    x: r2(x0 + 8 + rnd() * Math.max(0, x1 - x0 - 16)),
    y: r2(y0 + 1 + rnd() * Math.max(0, (y1 - y0) * 0.55)),
    len: r2(6 + rnd() * 6),
    angle: r2(left ? -tilt : 180 + tilt),
  };
}

/** Newest created_at of a wish that caught a star (0 = none). */
export function caughtStarAt(actions) {
  let at = 0;
  for (const a of rowsOf(actions)) {
    if (isWishRow(a) && a.data.caught_star === true) { const t = rowMs(a); if (t > at) at = t; }
  }
  return at;
}

/** Is a star card's catch window open? Its first 60s, and nobody caught it yet. */
export function starWishOpen(createdAt, nowMs, caughtAt = 0) {
  const t = typeof createdAt === 'number' ? createdAt : new Date(createdAt || 0).getTime();
  if (!Number.isFinite(t) || !t) return false;
  if (Number(caughtAt) >= t) return false;
  return Number(nowMs) - t < STAR_WINDOW_MS;
}

/** 🌠 The falling star: the newest shooting-star card of the running event,
 *  while catchable → { id, start, endsAt, elapsedSec }, else null. */
export function shootingStarFor(actions, liveEvent, nowMs) {
  if (!liveEvent || !liveEvent.template) return null;
  let star = null;
  for (const a of rowsOf(actions)) {
    if (!a || a.type !== 'shared_content' || !a.data || a.data.is_shooting_star !== true) continue;
    if (a.data.event_template !== liveEvent.template) continue;
    const t = rowMs(a);
    if (t && (!star || t > star.t)) star = { id: a.id, t };
  }
  if (!star || !starWishOpen(star.t, nowMs, caughtStarAt(actions))) return null;
  return { id: String(star.id || star.t), start: star.t, endsAt: star.t + STAR_WINDOW_MS, elapsedSec: Math.max(0, Number(nowMs) - star.t) / 1000 };
}

/** 🏮 One lantern per wish row from the last 90s, oldest first, capped. x is a
 *  hash of the row id (40-70%, over the quilts), so every viewer sees the same
 *  sky; the wish that caught the star gets the gold one. */
export function wishLanternsFor(actions, nowMs) {
  const out = [];
  for (const a of rowsOf(actions)) {
    if (!isWishRow(a)) continue;
    const t = rowMs(a);
    if (!t) continue;
    const el = Number(nowMs) - t;
    if (!(el < LANTERN_WINDOW_MS)) continue;
    const h = hash32(`${a.id || t}|lantern`);
    out.push({
      id: String(a.id || t), t,
      x: 40 + (h % 3001) / 100,
      rest: 22 + ((h >>> 11) % 2801) / 100, // where a still (reduced-motion) lantern hangs, y %
      sway: 4 + ((h >>> 5) % 301) / 100,    // seconds per sway
      elapsedSec: Math.max(0, el) / 1000,
      gold: a.data.caught_star === true,
    });
  }
  out.sort((p, q) => p.t - q.t);
  return out.slice(-LANTERN_MAX).map(({ t, ...l }) => ({ ...l, start: t }));
}

/** Everything the picture of `roomId` draws in its sky tonight, or null.
 *  The cloud rides the drawn moon (roof + porch); the rest need a sky box. */
export function nightSkyFor(roomId, actions, liveEvent, nowMs) {
  if (!roomId) return null;
  const box = SKY_BOXES[roomId] || null;
  const cloud = (MOON_SPOTS[roomId] && liveEvent) ? twistCloudFor(actions, liveEvent, nowMs) : null;
  const meteors = liveEvent ? meteorBoxFor(roomId, liveEvent.template) : null;
  const star = box ? shootingStarFor(actions, liveEvent, nowMs) : null;
  const lanterns = roomId === 'roof' ? wishLanternsFor(actions, nowMs) : [];
  if (!cloud && !meteors && !star && !lanterns.length) return null;
  return { cloud, meteors, star: star ? { ...star, box } : null, lanterns };
}

/** 🔭 Is a click at (x%, y%) of a w x h picture on the telescope? */
export function telescopeHit(xPct, yPct, w, h) {
  if (!(w > 0) || !(h > 0) || !Number.isFinite(Number(xPct)) || !Number.isFinite(Number(yPct))) return false;
  const dx = ((Number(xPct) - TELESCOPE.x) / 100) * w;
  const dy = ((Number(yPct) - TELESCOPE.y) / 100) * h;
  return Math.hypot(dx, dy) <= TELESCOPE.reach * w;
}

/** The telescope's aside on a moon night (the glass says where to click). */
export const TELESCOPE_MOON_ASIDE = 'Aimed right at the moon tonight. Have a look.';
/** Player copy: short, punchy, no em dashes. */
export const EYEPIECE_LINES = [
  "There's a rabbit up there making mochi. It never takes a night off.",
  'The rabbit is pounding rice cakes again. Somebody tell it to pace itself.',
  'Look close. The rabbit waves back if you wave first. Probably.',
];
export function eyepieceLine(dayKey, n = 0) {
  return EYEPIECE_LINES[(hash32(`${dayKey || ''}|eyepiece`) + Math.max(0, Math.floor(Number(n) || 0))) % EYEPIECE_LINES.length];
}

// ── 📰 09-26 the picture's news line (the full-screen picture covers the chat)
/** How long a house card stays under the picture. */
export const PICTURE_CARD_MS = 10 * 60 * 1000;

/** The newest event START card of the last 3h, ignoring any end card, so the
 *  picture can still show the wrap line ({ born, id, template, room } | null). */
export function recentEventStartFor(actions, nowMs, maxAgeMs = 3 * 60 * 60 * 1000) {
  let best = null;
  for (const a of rowsOf(actions)) {
    const d = (a && a.data) || {};
    if (!a || a.type !== 'space_command' || d.command !== 'house_event' || !d.is_event_start) continue;
    const born = rowMs(a);
    if (!(born > 0) || Number(nowMs) - born > maxAgeMs) continue;
    if (!best || born > best.born) best = { born, id: a.id, template: d.event_template || null, room: d.event_room || null };
  }
  return best;
}

/** The event's newest card since it started, under 10 min old: the start and
 *  ruling cards, the engine's notes (twist, star, wrap) and wish cards.
 *  → { key, text, star } | null; `star` = still catchable (offer the wish box). */
export function pictureCardFor(actions, event, nowMs) {
  if (!event || !(event.born > 0)) return null;
  let best = null;
  let bestAt = 0;
  for (const a of rowsOf(actions)) {
    const d = (a && a.data) || {};
    const at = rowMs(a);
    if (!(at >= event.born - 1000) || Number(nowMs) - at > PICTURE_CARD_MS || at < bestAt) continue;
    let text = null;
    if (a.type === 'space_command' && d.command === 'house_event') text = d.card_message;
    else if (a.type === 'shared_content' && d.is_house_event) text = d.description;
    else if (a.type === 'shared_content' && d.command === 'rv_snap') text = d.description; // 📸 a snap from the scene
    else if (isWishRow(a)) text = d.card_message;
    if (typeof text !== 'string' || !text.trim()) continue;
    best = { a, text: text.trim() };
    bestAt = at;
  }
  if (!best) return null;
  const star = !!(best.a.data && best.a.data.is_shooting_star === true) && starWishOpen(bestAt, nowMs, caughtStarAt(actions));
  return { key: best.a.id || String(bestAt), text: best.text, star };
}

/** The poll while its bell has not rung, else null. */
export function openPollFor(poll, nowMs) {
  if (!poll || !poll.id || !Array.isArray(poll.options) || !poll.options.length) return null;
  const closes = new Date(poll.closes_at || 0).getTime();
  if (Number.isFinite(closes) && closes > 0 && closes <= Number(nowMs)) return null;
  return poll;
}

// ── 🦝🌕 09-26 (Nina: "gerald on the moon ... would be HILARIOUS") ────────────
/** The glass over the moon on a moon night (only the glass shows him up there). */
export const MOON_GERALD_ASIDE = 'Wait. Is that Gerald? On the MOON?';
/** A click with the glass on him: caught. The 📸 then snaps him up there. */
export const MOON_GERALD_CAUGHT = '🦝 Caught him! Gerald is on the moon. Quick, 📸 before he floats off.';
/** How long after the catch the 📸 still snaps him. */
export const MOON_GERALD_SNAP_MS = 60 * 1000;
/** Is the glass centre (px in a w×h picture) over the drawn moon? */
export function glassOverMoon(center, moon, w, h) {
  if (!center || !moon || !(w > 0) || !(h > 0) || !(moon.size > 0)) return false;
  const dx = center.x - (moon.x / 100) * w;
  const dy = center.y - (moon.y / 100) * h;
  return Math.hypot(dx, dy) <= Math.max(18, (moon.size / 100) * w * 0.75);
}

// ── 📅🌕 09-26: the running event from the SERVER's calendar ────────────────
/** A chatty hour pushes the event's start card out of the loaded feed (the page
 *  loads ~100 rows; the moon night made ~270 in 50 min), and every scene piece
 *  keyed on that card vanished for late arrivals. The calendar payload's running
 *  row (houseCalendar: phase 'running' + started_at) is the fallback.
 *  → { born, id, template, room, label } | null */
export function runningEventFrom(cal, nowMs, maxAgeMs = 3 * 60 * 60 * 1000) {
  const rows = (cal && Array.isArray(cal.rows)) ? cal.rows : [];
  const r = rows.find((x) => x && x.phase === 'running' && typeof x.template === 'string' && x.template
    && Number(x.started_at) > 0 && Number(nowMs) - Number(x.started_at) >= 0 && Number(nowMs) - Number(x.started_at) <= maxAgeMs);
  return r ? { born: Number(r.started_at), id: `cal:${r.template}:${r.started_at}`, template: r.template, room: (typeof r.room === 'string' && r.room) ? r.room : null, label: r.label || null } : null;
}

// ── 💬 09-26 option C: THE SCENE'S CHAT AS SPEECH BUBBLES ────────────────────
// Nina 09-26: "I really like C, but only for certain events" (mockup
// https://claude.ai/artifact/FRhcSrwicgTVVW62KKdpFs). On the event's own scene
// the newest lines float over the picture, older ones fade, a message box sits
// on it, and "Show all N messages" opens the whole list. Scope RULED 09-26:
// sky nights · storm night · parties & socials · outings, NEVER movie nights or
// screenings (the couch chat is their chat). ONE reader: sceneBubblesOn. The
// set is pinned against the backend's own template flags (sceneChat.test.js
// requires houseEvents + housePlaces), so a new social or sky night cannot
// silently miss it. Zero network: the rows are the feed the page already holds.
export const BUBBLE_TEMPLATES = new Set([
  // sky nights (the house is out under them: housePlaces.SKY_GATHER_LINES)
  'moon_viewing', 'blood_moon', 'meteor_shower', 'comet_watch', 'aurora_night', 'fireworks_night',
  // storm night + the parties
  'storm_night', 'house_party', 'pair_celebration',
  // the socials (houseEvents `social: true`), movie night left out on purpose
  'grocery_run', 'bake_off', 'trivia_night', 'dance_off', 'talent_show', 'garage_sale', 'pet_show',
  'karaoke_night', 'brunch_club', 'regift_exchange', 'trick_or_treat', 'seance_night', 'costume_contest',
  'harvest_feast', 'snow_day', 'raccoon_census', 'landlord_inspection', 'board_game_invention',
  'open_mic_porch', 'mystery_smell', 'letters_to_the_next', 'spooky_story_night', 'mystery_night', 'doodle_night',
  // outings (the house is away; its place is the scene), the lighthouse screening left out
  'apple_picking',
  'maze_walk', // 🌽 09-28 maze: an outing, so bubbles by rule (sceneChat.test derives the set from the backend)
  'pumpkin_patch', // 🎃 10-02 pumpkins: an outing (dark until HOUSE_PUMPKINS_ENABLED; inert until one runs), bubbles by rule
]);
export function sceneBubblesOn(template) {
  return typeof template === 'string' && BUBBLE_TEMPLATES.has(template);
}

/** The room the running event's scene is in: its staged room (the invite's
 *  resolver), else, while the house is OUT on a bubble event, the outing's own
 *  place (outings stage no room: the orchard is where they are). A screening
 *  never gets one here, so the lighthouse picture stays exactly as it is. */
export function sceneRoomFor(event, eventRoom, outing) {
  if (!event) return null;
  if (typeof eventRoom === 'string' && eventRoom) return eventRoom;
  const id = outing && typeof outing.id === 'string' ? outing.id : '';
  return (id && sceneBubblesOn(event.template)) ? id : null;
}

export const BUBBLE_SHOWN = 3;    // the newest lines over the picture (the mockup's three)
export const BUBBLE_CHARS = 140;  // a bubble is a glance; the list shows the whole line
const CHAT_TYPES = new Set(['public_message', 'human_interjection']);

/** A line for a bubble: one paragraph, clamped at a word, and never a lone
 *  stage-direction star (it would italicize the rest of the line). */
export function bubbleText(text, max = BUBBLE_CHARS) {
  let s = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  const cap = Math.max(8, Number(max) || BUBBLE_CHARS);
  if (s.length > cap) {
    const cut = s.slice(0, cap - 1);
    const sp = cut.lastIndexOf(' ');
    s = `${(sp > cap * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s.,;:!?]+$/, '')}…`;
  }
  if ((s.match(/\*/g) || []).length % 2) { const i = s.lastIndexOf('*'); s = s.slice(0, i) + s.slice(i + 1); }
  return s;
}

/** Stage directions (*waves at the moon*) in italics, the rest plain → [{ t, i }]. */
export function bubbleParts(text) {
  const s = String(text == null ? '' : text);
  const out = [];
  const re = /\*([^*]+)\*/g;
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ t: s.slice(last, m.index), i: false });
    out.push({ t: m[1], i: true });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ t: s.slice(last), i: false });
  return out.filter((p) => p.t);
}

/** How far a bubble sits from the stack's left edge, % of the stack: a zig-zag
 *  every viewer sees the same (a hash of the line's key, never a random). */
export function bubbleNudge(key, span = 30) {
  const n = Math.max(0, Number(span) || 0);
  return n ? (hash32(`${key || ''}|bubble`) % (n * 10 + 1)) / 10 : 0;
}

/** The event scene's chat: every public line since the event began, oldest
 *  first. `seatOf(id)` → { name, ai } | null (the page's roster). Your own line
 *  shows the moment you send it (the optimistic row) and the real row takes its
 *  place under the same key (client_msg_id), so nothing blinks or doubles.
 *  → { lines: [{ key, name, ai, text, emote, at }], count } | null */
export function sceneChatFor(actions, event, seatOf = null) {
  if (!event || !(Number(event.born) > 0) || !sceneBubblesOn(event.template)) return null;
  const since = Number(event.born) - 1000;
  const byKey = new Map();
  for (const a of rowsOf(actions)) {
    if (!a || !CHAT_TYPES.has(a.type)) continue;
    const d = a.data || {};
    const text = typeof d.message === 'string' ? d.message.trim() : '';
    if (!text) continue;
    const at = rowMs(a);
    if (!(at >= since)) continue;
    const key = String(d.client_msg_id || a.id || `${a.game_instance_player_id || ''}|${at}`);
    const was = byKey.get(key);
    if (was && !was.optimistic) continue; // the real row already holds this key
    const seat = (typeof seatOf === 'function' && a.game_instance_player_id) ? seatOf(a.game_instance_player_id) : null;
    const name = (a.type === 'human_interjection'
      ? (d.sender_name || (seat && seat.name))
      : ((seat && seat.name) || d.sender_name)) || 'Someone';
    const ai = (seat && typeof seat.ai === 'boolean') ? seat.ai : a.type === 'public_message';
    byKey.set(key, { key, name: String(name), ai, text, emote: d.is_emote === true, at, optimistic: !!a.isOptimistic });
  }
  const lines = [...byKey.values()].sort((p, q) => p.at - q.at).map(({ optimistic, ...l }) => l);
  return { lines, count: lines.length };
}

/** The @names a scene line directs (the composer's own rule, HumanInputForm
 *  extractTargetPlayers: longest name first, any case, ends at a space, the
 *  line's end or punctuation), so "@Fen look up" reaches Fen from the scene
 *  exactly as it would from the chat box. `names` = everyone but you. */
export function mentionTargets(msg, names) {
  const text = String(msg || '');
  const list = (Array.isArray(names) ? names : []).filter((n) => typeof n === 'string' && n.trim());
  const out = [];
  for (const n of [...list].sort((a, b) => b.length - a.length)) {
    const re = new RegExp(`@${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$|[.,!?])`, 'i');
    if (re.test(text) && !out.includes(n)) out.push(n);
  }
  return out;
}

/** 📱 The phone scene: the painting COVERS the screen with the moon (or the
 *  middle) kept in view, clamped so no edge ever shows. `natural` = the art's
 *  own pixels (a 1536x672 guess until it loads). → { left, top, w, h } px | null */
export function stageBoxFor(vw, vh, natural, focus) {
  const W = Number(vw);
  const H = Number(vh);
  if (!(W > 0) || !(H > 0)) return null;
  const nw = natural && natural.w > 0 ? natural.w : 1536;
  const nh = natural && natural.h > 0 ? natural.h : 672;
  const s = Math.max(W / nw, H / nh);
  const w = Math.round(nw * s);
  const h = Math.round(nh * s);
  const fx = focus && Number.isFinite(Number(focus.x)) ? Number(focus.x) / 100 : 0.5;
  const fy = focus && Number.isFinite(Number(focus.y)) ? Number(focus.y) / 100 : 0.5;
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  return { left: Math.round(clamp(W / 2 - fx * w, W - w, 0)), top: Math.round(clamp(H / 2 - fy * h, H - h, 0)), w, h };
}
